import {
  WORKING_PAPER_NO_BALANCE,
  WORKING_PAPER_SYSTEM_GENERATED,
  type WorkingPaperDetail,
} from '@/core/models/phase2.models';
import type { JournalPrefill } from './JournalDialog';

/**
 * APT338 follow-up (the client, 28 Oct 2026 10:36): how a lead schedule's status reads on screen, in the client's own words. "NotStarted", "In Progress",
 * "Prepared" and "Reviewed" are shown as the API sends them (as before).
 */
export function workingPaperStatusLabel(status: string): string {
  if (status === WORKING_PAPER_NO_BALANCE) {
    return 'No Balance / No Work Required';
  }
  if (status === WORKING_PAPER_SYSTEM_GENERATED) {
    return 'System Generated';
  }
  return status;
}

/** The list's headings: consecutive schedules of the same group, in the order the API sends them (it already orders them by group and by the client's order). */
export function workingPaperGroups<T extends { group?: string }>(rows: readonly T[]): Array<{ name: string; rows: T[] }> {
  const groups: Array<{ name: string; rows: T[] }> = [];
  for (const row of rows) {
    const name = row.group ?? '';
    const last = groups[groups.length - 1];
    if (last && last.name === name) {
      last.rows.push(row);
    } else {
      groups.push({ name, rows: [row] });
    }
  }
  return groups;
}

/** The narrative of the adjustment journal a working paper starts. */
export function workingPaperJournalNarrative(scheduleName: string): string {
  return `${scheduleName} working paper - adjust ATB to schedule`;
}

/**
 * APT338 follow-up (the client, 28 Oct 2026 10:36: a "Create Adjustment Journal" option directly from the Working Paper "instead of requiring users to leave the
 * screen and navigate to Journals"; ATB 2,700, schedule 5,700, difference 3,000 -> journal -> ATB 5,700, difference 0): the adjustment journal that brings the
 * adjusted trial balance to the working paper. The schedule is the evidence, so it is the ATB that moves: the difference is schedule - ATB, and the ATB rises by it when a nominal is debited
 * and falls when it is credited (assets and expenses are positive in the trial balance, income and credits negative). The line for the schedule's own nominal is
 * pre-set only when exactly one nominal is mapped to its reporting codes; the counter line is the user's choice. Applied to the figures on screen, which may
 * include rows not yet saved - the API's WorkingPaperJournalRules.Lines applies the same rule to the saved ones.
 * Null when the schedule agrees, or the period takes no journal.
 */
export function workingPaperJournalPrefill(detail: WorkingPaperDetail, difference: number): JournalPrefill | null {
  const context = detail.journalContext;
  // To the penny, half away from zero, as the API rounds it (via the exponent so 10.005 is 10.01, not 10.00 as 10.005 * 100 would give).
  const amount = Math.round(Number(`${Math.abs(difference)}e2`)) / 100;
  if (!context || detail.isLocked || amount < 0.005) {
    return null;
  }

  const only = context.nominals.length === 1 ? context.nominals[0] : null;
  const raise = difference > 0;

  return {
    typeCode: context.typeCode,
    journalDate: context.journalDate,
    narrative: workingPaperJournalNarrative(detail.name),
    lines: [
      { nominalAccountId: only?.nominalAccountId ?? null, debit: raise ? amount : 0, credit: raise ? 0 : amount, narrative: null },
      { nominalAccountId: null, debit: raise ? 0 : amount, credit: raise ? amount : 0, narrative: null },
    ],
  };
}
